import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Select from '../../../components/ui/Select';

const ClinicalOutcomes = ({ treatmentId, data, onUpdate }) => {
  const [outcomes, setOutcomes] = useState({
    painLevel: data?.painLevel || null,
    functionalImprovement: data?.functionalImprovement || null,
    aestheticSatisfaction: data?.aestheticSatisfaction || null,
    qualityOfLife: data?.qualityOfLife || null,
    eatingAbility: data?.eatingAbility || null,
    speechClarity: data?.speechClarity || null,
    sleepQuality: data?.sleepQuality || null,
    socialConfidence: data?.socialConfidence || null,
    symptomResolution: data?.symptomResolution || [],
    sideEffects: data?.sideEffects || [],
    additionalNotes: data?.additionalNotes || ''
  });

  useEffect(() => {
    onUpdate(outcomes);
  }, [outcomes]);

  const handleOutcomeChange = (field, value) => {
    setOutcomes(prev => ({ ...prev, [field]: value }));
  };

  const painLevelOptions = [
    { value: '0', label: '0 - No Pain' },
    { value: '1-2', label: '1-2 - Mild Pain' },
    { value: '3-4', label: '3-4 - Moderate Pain' },
    { value: '5-6', label: '5-6 - Moderately Severe Pain' },
    { value: '7-8', label: '7-8 - Severe Pain' },
    { value: '9-10', label: '9-10 - Worst Possible Pain' }
  ];

  const improvementOptions = [
    { value: 'significant', label: 'Significant Improvement' },
    { value: 'moderate', label: 'Moderate Improvement' },
    { value: 'slight', label: 'Slight Improvement' },
    { value: 'no-change', label: 'No Change' },
    { value: 'worse', label: 'Condition Worsened' }
  ];

  const satisfactionOptions = [
    { value: 'very-satisfied', label: 'Very Satisfied' },
    { value: 'satisfied', label: 'Satisfied' },
    { value: 'neutral', label: 'Neutral' },
    { value: 'dissatisfied', label: 'Dissatisfied' },
    { value: 'very-dissatisfied', label: 'Very Dissatisfied' }
  ];

  const symptomOptions = [
    { value: 'pain', label: 'Pain/Discomfort' },
    { value: 'swelling', label: 'Swelling' },
    { value: 'bleeding', label: 'Bleeding' },
    { value: 'sensitivity', label: 'Sensitivity' },
    { value: 'difficulty-chewing', label: 'Difficulty Chewing' },
    { value: 'jaw-stiffness', label: 'Jaw Stiffness' },
    { value: 'bad-breath', label: 'Bad Breath' },
    { value: 'loose-teeth', label: 'Loose Teeth' }
  ];

  const sideEffectOptions = [
    { value: 'none', label: 'No Side Effects' },
    { value: 'mild-discomfort', label: 'Mild Discomfort' },
    { value: 'swelling', label: 'Swelling' },
    { value: 'bruising', label: 'Bruising' },
    { value: 'numbness', label: 'Numbness/Tingling' },
    { value: 'infection', label: 'Signs of Infection' },
    { value: 'allergic-reaction', label: 'Allergic Reaction' },
    { value: 'other', label: 'Other (specify in notes)' }
  ];

  const PainScale = ({ value, onChange }) => {
    const painFaces = [
      { level: 0, icon: 'Smile', color: 'text-success', label: 'No Pain' },
      { level: 2, icon: 'Meh', color: 'text-primary', label: 'Mild' },
      { level: 4, icon: 'Frown', color: 'text-warning', label: 'Moderate' },
      { level: 6, icon: 'Frown', color: 'text-warning', label: 'Severe' },
      { level: 8, icon: 'Frown', color: 'text-error', label: 'Very Severe' },
      { level: 10, icon: 'Frown', color: 'text-error', label: 'Worst' }
    ];

    return (
      <div className="space-y-4">
        <label className="text-sm font-medium text-foreground">Current Pain Level</label>
        <div className="grid grid-cols-3 md:grid-cols-6 gap-4">
          {painFaces?.map(face => (
            <button
              key={face?.level}
              type="button"
              onClick={() => onChange(face?.level?.toString())}
              className={`flex flex-col items-center gap-2 p-4 rounded-lg border-2 transition-all ${
                value === face?.level?.toString()
                  ? 'border-primary bg-primary/10' :'border-border bg-background hover:border-primary/50'
              }`}
            >
              <Icon name={face?.icon} size={32} className={face?.color} />
              <span className="text-xs font-medium text-foreground">{face?.level}</span>
              <span className="text-xs text-muted-foreground">{face?.label}</span>
            </button>
          ))}
        </div>
      </div>
    );
  };

  const getImprovementColor = (value) => {
    switch (value) {
      case 'significant':
        return 'bg-success/10 text-success border-success';
      case 'moderate':
        return 'bg-primary/10 text-primary border-primary';
      case 'slight':
        return 'bg-warning/10 text-warning border-warning';
      case 'worse':
        return 'bg-error/10 text-error border-error';
      default:
        return 'bg-muted text-muted-foreground border-border';
    }
  };

  return (
    <div className="space-y-8">
      <div>
        <h3 className="text-lg font-semibold text-foreground mb-2 flex items-center gap-2">
          <Icon name="Activity" size={20} className="text-primary" />
          Pain Assessment
        </h3>
        <p className="text-sm text-muted-foreground mb-6">
          Rate your current pain level compared to before treatment
        </p>
        <PainScale
          value={outcomes?.painLevel}
          onChange={(value) => handleOutcomeChange('painLevel', value)}
        />
      </div>

      <div className="border-t border-border pt-6">
        <h3 className="text-lg font-semibold text-foreground mb-6 flex items-center gap-2">
          <Icon name="TrendingUp" size={20} className="text-primary" />
          Functional Improvements
        </h3>
        <div className="space-y-4">
          <Select
            label="Overall Functional Improvement"
            placeholder="Select improvement level"
            options={improvementOptions}
            value={outcomes?.functionalImprovement}
            onChange={(value) => handleOutcomeChange('functionalImprovement', value)}
          />
          {outcomes?.functionalImprovement && (
            <div className={`rounded-lg border-2 p-4 ${getImprovementColor(outcomes?.functionalImprovement)}`}>
              <p className="text-sm font-medium">
                {improvementOptions?.find(opt => opt?.value === outcomes?.functionalImprovement)?.label}
              </p>
            </div>
          )}
        </div>
      </div>

      <div className="border-t border-border pt-6">
        <h3 className="text-lg font-semibold text-foreground mb-6 flex items-center gap-2">
          <Icon name="Sparkles" size={20} className="text-primary" />
          Aesthetic Satisfaction
        </h3>
        <Select
          label="How satisfied are you with the aesthetic outcome?"
          placeholder="Select satisfaction level"
          options={satisfactionOptions}
          value={outcomes?.aestheticSatisfaction}
          onChange={(value) => handleOutcomeChange('aestheticSatisfaction', value)}
        />
      </div>

      <div className="border-t border-border pt-6">
        <h3 className="text-lg font-semibold text-foreground mb-6 flex items-center gap-2">
          <Icon name="Heart" size={20} className="text-primary" />
          Quality of Life Impact
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Select
            label="Eating Ability"
            placeholder="Select improvement"
            options={improvementOptions}
            value={outcomes?.eatingAbility}
            onChange={(value) => handleOutcomeChange('eatingAbility', value)}
          />
          <Select
            label="Speech Clarity"
            placeholder="Select improvement"
            options={improvementOptions}
            value={outcomes?.speechClarity}
            onChange={(value) => handleOutcomeChange('speechClarity', value)}
          />
          <Select
            label="Sleep Quality"
            placeholder="Select improvement"
            options={improvementOptions}
            value={outcomes?.sleepQuality}
            onChange={(value) => handleOutcomeChange('sleepQuality', value)}
          />
          <Select
            label="Social Confidence"
            placeholder="Select improvement"
            options={improvementOptions}
            value={outcomes?.socialConfidence}
            onChange={(value) => handleOutcomeChange('socialConfidence', value)}
          />
        </div>
      </div>

      <div className="border-t border-border pt-6">
        <h3 className="text-lg font-semibold text-foreground mb-6 flex items-center gap-2">
          <Icon name="CheckCircle" size={20} className="text-primary" />
          Symptom Resolution
        </h3>
        <Select
          label="Which symptoms have been resolved or improved?"
          placeholder="Select all that apply"
          options={symptomOptions}
          value={outcomes?.symptomResolution}
          onChange={(value) => handleOutcomeChange('symptomResolution', value)}
          multiple
        />
      </div>

      <div className="border-t border-border pt-6">
        <h3 className="text-lg font-semibold text-foreground mb-6 flex items-center gap-2">
          <Icon name="AlertCircle" size={20} className="text-warning" />
          Side Effects
        </h3>
        <Select
          label="Have you experienced any side effects?"
          placeholder="Select all that apply"
          options={sideEffectOptions}
          value={outcomes?.sideEffects}
          onChange={(value) => handleOutcomeChange('sideEffects', value)}
          multiple
        />
      </div>

      <div className="border-t border-border pt-6">
        <h3 className="text-lg font-semibold text-foreground mb-4 flex items-center gap-2">
          <Icon name="FileText" size={20} className="text-primary" />
          Additional Clinical Notes
        </h3>
        <textarea
          value={outcomes?.additionalNotes}
          onChange={(e) => handleOutcomeChange('additionalNotes', e?.target?.value)}
          placeholder="Please describe any other symptoms, improvements, or concerns..."
          rows={6}
          className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
        />
      </div>

      <div className="bg-warning/10 rounded-lg border border-warning/20 p-4">
        <div className="flex items-start gap-3">
          <Icon name="AlertTriangle" size={20} className="text-warning flex-shrink-0 mt-0.5" />
          <div className="text-sm text-muted-foreground">
            <p className="font-medium text-foreground mb-1">Important</p>
            <p>If you are experiencing severe pain, infection signs, or other urgent symptoms, please contact your provider immediately rather than waiting for survey review.</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ClinicalOutcomes;